// session-share-api.ts — typed client for the session-sharing REST API (the
// Google-Docs-style chat share feature). These endpoints are manager-native
// (NOT proxied to the agent container) and live at the manager root — identity
// comes from the Auth0 bearer (auth.md), not a `/users/:user` URL segment — so
// we reuse the same base-url + auth-header helpers as lib/api.ts and mirror the
// conventions of marketplace-api.ts.
//
// `owner` query/body values are Auth0 subs that can contain characters like `|`,
// so we always encodeURIComponent() them in paths and query strings.

import { API_ORIGIN, jsonOrThrow, authHeaders } from './api';
import type { HistoryEntry } from './agent-protocol';

// JSON-bodied requests merge the bearer with the content-type header (same
// pattern as marketplace-api.ts).
async function jsonHeaders(): Promise<Record<string, string>> {
	return { ...(await authHeaders()), 'content-type': 'application/json' };
}

// ── DTOs (share REST contract) ────────────────────────────────────────────────

// A user pickable from the FULL Auth0 directory (incl. people who never logged
// in). name/email may be null when Auth0 resolution is unavailable.
export type ShareableUser = {
	sub: string;
	name: string | null;
	email: string | null;
};

// One share row on an owned chat. `orgWide:true` means the whole-org grant (a
// single such row); `granteeSub` carries the user id for a per-person grant.
export type SessionShareView = {
	id: number;
	granteeSub: string | null;
	orgWide: boolean;
	name: string | null;
	email: string | null;
	createdAt: number;
};

// A chat that someone has shared WITH the current user (read-only viewer source).
export type SharedSessionView = {
	sessionId: string;
	ownerSub: string;
	ownerName: string | null;
	title: string | null;
	sharedAt: number;
	sidebarVisible?: boolean;
};

// The read-only transcript of a shared chat. `entries` reuse the same HistoryEntry
// shape the normal chat history uses, so the existing renderer can consume them.
export type SharedHistoryResponse = {
	entries: HistoryEntry[];
	title: string | null;
	ownerSub: string;
	ownerName: string | null;
};

// ── endpoints ─────────────────────────────────────────────────────────────────
// jsonOrThrow is shared from lib/api.ts (same error-unwrap contract).

// Autocomplete the Auth0 directory. `q` empty → a default roster page. Debounce
// at the call site (~250ms).
export async function searchDirectory(q: string): Promise<ShareableUser[]> {
	const res = await fetch(`${API_ORIGIN}/directory/users?q=${encodeURIComponent(q)}`, {
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<{ users: ShareableUser[] }>(res, 'searchDirectory');
	return body.users;
}

// Current people + org row for an owned chat.
export async function getSessionShares(sessionId: string): Promise<SessionShareView[]> {
	const res = await fetch(`${API_ORIGIN}/sessions/${encodeURIComponent(sessionId)}/shares`, {
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<{ shares: SessionShareView[] }>(res, 'getSessionShares');
	return body.shares;
}

// Add people and/or turn on org-wide. Returns the updated list.
export async function addSessionShares(
	sessionId: string,
	input: { subs?: string[]; org?: boolean },
): Promise<SessionShareView[]> {
	const res = await fetch(`${API_ORIGIN}/sessions/${encodeURIComponent(sessionId)}/shares`, {
		method: 'POST',
		headers: await jsonHeaders(),
		body: JSON.stringify(input),
	});
	const body = await jsonOrThrow<{ shares: SessionShareView[] }>(res, 'addSessionShares');
	return body.shares;
}

// Remove one row — a person OR the org row — by its numeric id. 204 on success.
export async function removeSessionShare(sessionId: string, shareId: number): Promise<void> {
	const res = await fetch(
		`${API_ORIGIN}/sessions/${encodeURIComponent(sessionId)}/shares/${shareId}`,
		{ method: 'DELETE', headers: await authHeaders() },
	);
	// 204 on success; tolerate 404 so an already-gone share resolves cleanly.
	if (!res.ok && res.status !== 404) throw new Error(`removeSessionShare: ${res.status}`);
}

// Chats shared WITH me (the sidebar "Shared with me" section + viewer source).
export async function getSharedWithMe(): Promise<SharedSessionView[]> {
	const res = await fetch(`${API_ORIGIN}/sessions/shared-with-me`, {
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<{ shared: SharedSessionView[] }>(res, 'getSharedWithMe');
	return body.shared;
}

// Read-only transcript of a chat shared with me. `owner` is the owner's Auth0
// sub (encodeURIComponent'd — may contain `|`). 403 = not shared / revoked.
export async function getSharedHistory(
	sessionId: string,
	ownerSub: string,
): Promise<SharedHistoryResponse> {
	const res = await fetch(
		`${API_ORIGIN}/sessions/${encodeURIComponent(sessionId)}/shared-history?owner=${encodeURIComponent(ownerSub)}`,
		{ headers: await authHeaders() },
	);
	return jsonOrThrow<SharedHistoryResponse>(res, 'getSharedHistory');
}

// Fork a shared chat into MY chats (a copy I can continue). Returns the new
// owned session id. `owner` is the owner's Auth0 sub.
export async function forkSharedSession(sessionId: string, ownerSub: string): Promise<string> {
	const res = await fetch(`${API_ORIGIN}/sessions/${encodeURIComponent(sessionId)}/fork`, {
		method: 'POST',
		headers: await jsonHeaders(),
		body: JSON.stringify({ owner: ownerSub }),
	});
	const body = await jsonOrThrow<{ sessionId: string }>(res, 'forkSharedSession');
	return body.sessionId;
}

// "New chat with a recap" (Quiet Ring, docs/ui-revamp/approved/composer.md):
// fork MY OWN chat into a fresh session seeded with a short AI-generated recap
// (goals, key facts, open items — not the full transcript). The recap lands as
// the visible first message of the new chat. Throws on failure — the composer
// popover shows the in-place "Try again".
export async function forkSessionWithRecap(sessionId: string): Promise<string> {
	const res = await fetch(
		`${API_ORIGIN}/sessions/${encodeURIComponent(sessionId)}/fork-with-recap`,
		{ method: 'POST', headers: await jsonHeaders() },
	);
	const body = await jsonOrThrow<{ sessionId: string }>(res, 'forkSessionWithRecap');
	return body.sessionId;
}
